import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider as SsoAuthProvider } from "@next-social-platform/chatbot_sso/react";

import { LookProvider } from "./shared/design/LookProvider";
import { isDesignPreviewEnabled } from "./pages/design-preview/designPreview";
import { MyProSidebarProvider } from "./pages/global/sidebar/sidebarContext";
import { AuthProvider } from "./context/AuthContext";
import { BusinessProvider } from "./context/BusinessContext";
import { buildOidcConfig, selectInitOptions } from "./auth/oidcConfig";
import RequireAuth from "./components/RequireAuth";
import RequireBusinessSelection from "./components/RequireBusinessSelection";
import RequireBookingAdminRoute from "./components/RequireBookingAdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ToastProvider";
import PageSkeleton from "./components/PageSkeleton";
import GlobalShortcuts from "./components/GlobalShortcuts";

import Topbar from "./pages/global/Topbar";
import ImpersonationBanner from "./components/ImpersonationBanner";

// Lazy-load every page so the cold-load JS bundle doesn't ship 21 page modules
// up-front. Each `lazy()` boundary becomes its own chunk; <Suspense> shows
// PageSkeleton until the chunk lands (typically <200ms on warm cache).
const Dashboard = lazy(() => import("./pages/dashboard"));
const Business = lazy(() => import("./pages/business"));
const Team = lazy(() => import("./pages/team"));
const Staff = lazy(() => import("./pages/staff"));
const CreateStaff = lazy(() => import("./pages/staff/create"));
const AcceptStaffInvitation = lazy(() => import("./pages/staff/AcceptInvitation"));
const CreateSpace = lazy(() => import("./pages/space/create"));
const CreateResource = lazy(() => import("./pages/resource/create"));
const Category = lazy(() => import("./pages/category"));
const Branches = lazy(() => import("./pages/branches"));
const HoursPage = lazy(() => import("./pages/hours/HoursPage"));
const LimitsPage = lazy(() => import("./pages/limits/LimitsPage"));
const AddOnsPage = lazy(() => import("./pages/addOns/AddOnsPage"));
const KioskSettingsPage = lazy(() => import("./pages/kioskSettings/KioskSettingsPage"));
const BranchDetailPage = lazy(
  () => import("./pages/branch/BranchDetailPage"),
);
const UsersPage = lazy(() => import("./pages/users"));
const Calendar = lazy(() => import("./pages/calendar"));
const BusinessEdit = lazy(() => import("./pages/BusinessProfile"));
const BusinessMembersPage = lazy(() => import("./pages/business/members"));
const Service = lazy(() => import("./pages/service"));
const ServiceForm = lazy(() => import("./pages/service/form"));
const LegacyServiceTab = lazy(() => import("./pages/service/LegacyServiceTab"));
const CreateServiceWizard = lazy(() => import("./pages/service/create/CreateServiceWizard"));
const Offerings = lazy(() => import("./pages/offering"));
const CohortsBrowserPage = lazy(
  () => import("./pages/cohort/CohortsBrowserPage"),
);
const CohortDetailPage = lazy(
  () => import("./pages/cohort/CohortDetailPage"),
);
const CohortCreatePage = lazy(
  () => import("./pages/cohort/CohortCreatePage"),
);
const AllBusinessesPage = lazy(() => import("./pages/admin/businesses"));
const BusinessRolesPage = lazy(() => import("./pages/admin/business-roles"));
const BusinessTypesPage = lazy(() => import("./pages/admin/business-types"));
const ScheduleAuditPage = lazy(() => import("./pages/admin/audit"));
const ServiceAuditPage = lazy(() => import("./pages/admin/audit/services"));
const AdminLookPage = lazy(() => import("./pages/admin/AdminLookPage"));
const BookingsListPage = lazy(() => import("./pages/bookings"));
const FrontDeskPage = lazy(() => import("./pages/frontdesk"));
const BookingIdRedirect = lazy(() => import("./pages/bookings/BookingIdRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DesignPreviewPage = lazy(() => import("./pages/design-preview/DesignPreviewPage"));
const KioskPage = lazy(() => import("./pages/kiosk/KioskPage"));

// SDK provider owns the Auth instance + init() + event subscription.
// Config + initOptions resolved once at module load — the SDK provider
// intentionally does not watch initOptions for changes (changing it
// mid-life would clobber the live session).
const oidcConfig = buildOidcConfig();
const initOptions = selectInitOptions();

const App = (): JSX.Element => {
  return (
    <Routes>
      {/* Invitation accept callback lives OUTSIDE the auth wrappers.
          Chatbot_sso redirects here with ?acceptance=<jwt> right after
          signup, before the recipient has any admin session. If this
          route were inside RequireAuth, the OIDC redirect would drop
          the ?acceptance query during the sign-in dance (OIDC
          redirect_uri must match the registered one — no extra query
          params survive). Rendering before SsoAuthProvider/RequireAuth
          keeps the JWT in the URL and lets the page POST to the public
          backend endpoint directly. After success, the page redirects
          to /business which triggers the normal OIDC flow. */}
      <Route
        path="/staff/invitations/accept"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <AcceptStaffInvitation />
          </Suspense>
        }
      />
      {/* Dev-only screenshots of every look (no sign-in, no backend). */}
      {isDesignPreviewEnabled(process.env.NODE_ENV) ? (
        <Route
          path="/design-preview"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DesignPreviewPage />
            </Suspense>
          }
        />
      ) : null}
      {/* Salon tablet: outside the admin's SSO provider — stylists sign in by phone QR, in memory only. */}
      <Route
        path="/kiosk/:businessId/:branchId"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <KioskPage />
          </Suspense>
        }
      />
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  );
};

const AuthenticatedApp = (): JSX.Element => {
  return (
    <SsoAuthProvider config={oidcConfig} initOptions={initOptions}>
      <AuthProvider oidcInitWhenUnauthenticated="login-required">
        <RequireAuth>
        <LookProvider>
            <ToastProvider>
              <MyProSidebarProvider>
                <div style={{ height: "100%", width: "100%" }}>
                  <main>
                    <BusinessProvider>
                      <ImpersonationBanner />
                      <Topbar />
                      <GlobalShortcuts />
                      <ErrorBoundary>
                        <Suspense fallback={<PageSkeleton />}>
                          <Routes>
                            <Route path="/business" element={<Business />} />

                            {/* Business-scoped operator pages — require an active
                                business in `BusinessContext`. */}
                            <Route element={<RequireBusinessSelection />}>
                              <Route path="/" element={<Dashboard />} />
                              <Route path="/staff" element={<Staff />} />
                              <Route path="/team" element={<Team />} />

                              {/* Normalized CRUD routes: /new for create,
                                  /:id for edit. Old /create routes stay as
                                  redirects so external bookmarks still work. */}
                              <Route path="/staff/new" element={<CreateStaff />} />
                              {/* Schedule & availability sub-tabs must precede
                                  /staff/:id so the more specific path wins. */}
                              <Route
                                path="/staff/:id/schedule"
                                element={<CreateStaff />}
                              />
                              <Route
                                path="/staff/:id/availability"
                                element={<CreateStaff />}
                              />
                              <Route path="/staff/:id" element={<CreateStaff />} />
                              <Route
                                path="/staff/create"
                                element={<Navigate to="/staff/new" replace />}
                              />
                              <Route path="/space/new" element={<CreateSpace />} />
                              {/* Schedule & availability sub-tabs must precede
                                  /space/:id so the more specific path wins. */}
                              <Route
                                path="/space/:id/schedule"
                                element={<CreateSpace />}
                              />
                              <Route
                                path="/space/:id/availability"
                                element={<CreateSpace />}
                              />
                              <Route path="/space/:id" element={<CreateSpace />} />
                              <Route
                                path="/space/create"
                                element={<Navigate to="/space/new" replace />}
                              />
                              <Route path="/resource/new" element={<CreateResource />} />
                              <Route
                                path="/resource/:id/schedule"
                                element={<CreateResource />}
                              />
                              <Route
                                path="/resource/:id/availability"
                                element={<CreateResource />}
                              />
                              <Route path="/resource/:id" element={<CreateResource />} />
                              <Route
                                path="/resource/create"
                                element={<Navigate to="/resource/new" replace />}
                              />
                              <Route path="/branch" element={<Branches />} />
                              {/* Branch detail with URL-driven tabs
                                  (mirrors /service/:id/* pattern). The
                                  /branch list page still uses the modal
                                  edit dialog for basic CRUD; this page
                                  owns per-branch configuration depth
                                  (offerings, venue hours, staff roster,
                                  spaces, resources). */}
                              <Route path="/hours" element={<HoursPage />} />
                              <Route path="/limits" element={<LimitsPage />} />
                              <Route path="/add-ons" element={<AddOnsPage />} />
                              <Route path="/kiosk-settings" element={<KioskSettingsPage />} />
                              <Route path="/branch/:id" element={<BranchDetailPage />} />
                              <Route path="/branch/:id/schedule" element={<BranchDetailPage />} />
                              <Route path="/branch/:id/staff" element={<BranchDetailPage />} />
                              <Route path="/branch/:id/spaces" element={<BranchDetailPage />} />
                              <Route path="/branch/:id/resources" element={<BranchDetailPage />} />
                              <Route path="/category" element={<Category />} />
                              <Route path="/service" element={<Service />} />
                              <Route path="/service/new" element={<CreateServiceWizard />} />
                              <Route
                                path="/service/create"
                                element={<Navigate to="/service/new" replace />}
                              />
                              {/* SESSIONS-mode services use this admin page
                                  instead of the recurring schedule editor —
                                  operator pre-creates individual sessions
                                  (one row per offered slot) with optional
                                  per-session capacity overrides. */}
                              <Route
                                path="/service/:serviceId/cohorts"
                                element={<CohortsBrowserPage />}
                              />
                              <Route
                                path="/service/:serviceId/cohorts/new"
                                element={<CohortCreatePage />}
                              />
                              <Route
                                path="/cohort/:id"
                                element={<CohortDetailPage />}
                              />
                              {/* Legacy sessions URL — redirects to cohort
                                  browser. Per Q7 aggressive refactor, the
                                  old per-occurrence SessionsManagementPage
                                  is deleted; cohorts replace it. */}
                              <Route
                                path="/service/:serviceId/sessions"
                                element={<CohortsBrowserPage />}
                              />
                              {/* URL-driven tabs (mirror /staff, /space, /resource):
                                  /service/:id/<tab> renders ServiceForm with the
                                  matching tab active. Deep-linkable, back-button
                                  navigates between tabs. Tab order matches the
                                  TabId union in form.tsx. */}
                              <Route path="/service/:id" element={<ServiceForm />} />
                              <Route path="/service/:id/rules" element={<ServiceForm />} />
                              <Route path="/service/:id/who" element={<ServiceForm />} />
                              <Route path="/service/:id/payment" element={<ServiceForm />} />
                              <Route path="/service/:id/branches" element={<ServiceForm />} />
                              <Route path="/service/:id/notify" element={<ServiceForm />} />
                              {/* Pre-C3 tab URLs redirect to the new tabs. */}
                              <Route path="/service/:id/booking" element={<LegacyServiceTab slug="booking" />} />
                              <Route path="/service/:id/schedule" element={<LegacyServiceTab slug="schedule" />} />
                              <Route path="/service/:id/resources" element={<LegacyServiceTab slug="resources" />} />
                              <Route path="/service/:id/pricing" element={<LegacyServiceTab slug="pricing" />} />
                              <Route path="/service/:id/customer-fields" element={<LegacyServiceTab slug="customer-fields" />} />
                              <Route path="/service/:id/notifications" element={<LegacyServiceTab slug="notifications" />} />
                              {/* Power-user view of every ServiceBranchOffering
                                  in the current business. See spec:
                                  backend/docs/multi-branch-offerings-spec.md */}
                              <Route path="/offerings" element={<Offerings />} />
                              <Route path="/users" element={<UsersPage />} />
                              <Route
                                path="/admin/users"
                                element={<Navigate to="/users" replace />}
                              />
                              <Route path="/business/update/*" element={<BusinessEdit />} />
                              {/* In-business operator management. Page is open
                                  to any authenticated member; backend gates
                                  write ops (create/update/remove role) by
                                  OWNER/ADMIN of THIS business. See
                                  /business-membership/self/* endpoints. */}
                              <Route path="/business/members" element={<BusinessMembersPage />} />
                              <Route path="/faq" element={<Navigate to="/" replace />} />
                              <Route path="/calendar" element={<Calendar />} />
                              {/* Bookings section — one page, view driven by URL
                                  so the toggle is deep-linkable. Calendar is the
                                  default (operator daily driver); the table lives
                                  at /bookings/list. Bare /bookings → calendar. */}
                              <Route path="/front-desk" element={<FrontDeskPage />} />
                              <Route path="/bookings" element={<BookingsListPage />} />
                              <Route
                                path="/bookings/calendar"
                                element={<BookingsListPage />}
                              />
                              <Route
                                path="/bookings/list"
                                element={<BookingsListPage />}
                              />
                              <Route path="/bookings/:id" element={<BookingIdRedirect />} />
                            </Route>

                            {/* Admin-only routes — route-level guard avoids the
                                flash-of-content the in-page guards had. */}
                            <Route element={<RequireBookingAdminRoute />}>
                              <Route path="/admin/businesses" element={<AllBusinessesPage />} />
                              <Route path="/admin/business-roles" element={<BusinessRolesPage />} />
                              <Route path="/admin/business-types" element={<BusinessTypesPage />} />
                              <Route path="/admin/audit" element={<ScheduleAuditPage />} />
                              <Route path="/admin/audit/services" element={<ServiceAuditPage />} />
                              <Route path="/admin/look" element={<AdminLookPage />} />
                            </Route>

                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </ErrorBoundary>
                    </BusinessProvider>
                  </main>
                </div>
              </MyProSidebarProvider>
            </ToastProvider>
        </LookProvider>
      </RequireAuth>
      </AuthProvider>
    </SsoAuthProvider>
  );
};

export default App;
